import moment from 'moment'
import { groupBy } from 'lib/array/arrayUtils'
import { type CruiseDeparturePriceTotals, getCruiseDeparturePrices, isCruiseDepartureSoldOut } from './cruiseDeparturePricing'

export type CruiseDepartureSort =
  | 'departure-soon'
  | 'price-asc'
  | 'price-desc'

export type DeparturesByDate = Record<string, Array<App.CruiseDepartureDetails>>

export interface CruiseDepartureSortingData {
  lowestPrice: number
  highestPrice: number
  dateUnix: number
  year: number
  hasPrice: boolean
}

export interface CruiseDepartureListItem {
  dateLabel: string
  departure: App.CruiseDepartureDetails
  sortingData: CruiseDepartureSortingData
}

export type CruiseDeparturesByMonth = Array<[
  dateLabel: string,
  departures: Array<CruiseDepartureListItem>,
]>

export function getCruiseDepartureList(
  departuresByDate: DeparturesByDate,
  lowestDeparturePricesByCategory: CruiseDeparturePriceTotals,
  formatMonthLabel: (date: moment.Moment) => string,
): Array<CruiseDepartureListItem> {
  return Object.values(departuresByDate)
    .flat()
    .map((departure) => {
      const prices = getCruiseDeparturePrices(lowestDeparturePricesByCategory, departure.id)
      const date = moment(departure.departureDate)
      const hasPrice = !isCruiseDepartureSoldOut(lowestDeparturePricesByCategory, departure.id)

      return {
        dateLabel: formatMonthLabel(date),
        departure,
        sortingData: {
          lowestPrice: hasPrice ? Math.min(...prices) : Number.POSITIVE_INFINITY,
          highestPrice: hasPrice ? Math.max(...prices) : Number.NEGATIVE_INFINITY,
          dateUnix: date.unix(),
          year: date.year(),
          hasPrice,
        },
      }
    })
}

function compareByDate(
  first: CruiseDepartureListItem,
  second: CruiseDepartureListItem,
): number {
  return first.sortingData.dateUnix - second.sortingData.dateUnix
}

function compareByPrice(
  first: CruiseDepartureListItem,
  second: CruiseDepartureListItem,
  direction: 'asc' | 'desc',
): number {
  if (first.sortingData.hasPrice && second.sortingData.hasPrice) {
    const firstPrice = direction === 'asc' ? first.sortingData.lowestPrice : first.sortingData.highestPrice
    const secondPrice = direction === 'asc' ? second.sortingData.lowestPrice : second.sortingData.highestPrice
    return (direction === 'asc' ? firstPrice - secondPrice : secondPrice - firstPrice) || compareByDate(first, second)
  }

  if (first.sortingData.hasPrice) return -1
  if (second.sortingData.hasPrice) return 1

  return compareByDate(first, second)
}

export function sortCruiseDepartureList(
  departures: Array<CruiseDepartureListItem>,
  sort: CruiseDepartureSort | undefined,
): Array<CruiseDepartureListItem> {
  return [...departures].sort((first, second) => {
    if (sort === 'price-asc') return compareByPrice(first, second, 'asc')
    if (sort === 'price-desc') return compareByPrice(first, second, 'desc')
    return compareByDate(first, second)
  })
}

export function getCruiseDepartureYearOptions(
  departures: Array<CruiseDepartureListItem>,
): Array<number> {
  return Array.from(new Set(departures.map((item) => item.sortingData.year)))
    .sort((first, second) => first - second)
}

export function getCruiseDepartureCountByYear(
  departures: Array<CruiseDepartureListItem>,
): Record<number, number> {
  return departures.reduce<Record<number, number>>((counts, item) => {
    const year = item.sortingData.year
    counts[year] = (counts[year] ?? 0) + 1
    return counts
  }, {})
}

export function groupCruiseDeparturesByMonth(
  departures: Array<CruiseDepartureListItem>,
): CruiseDeparturesByMonth {
  return Array.from(groupBy(departures, (item) => item.dateLabel).entries())
}
